import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getCheckoutStatus } from '../api/billing.js';
import { useToast } from '../components/Toast.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';

const POLL_INTERVAL_MS = 2000;
const TIMEOUT_MS = 30000;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { fetchUsage } = useAuth();

  const [status, setStatus] = useState('polling');
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      navigate('/pricing');
      return;
    }

    async function checkStatus() {
      try {
        const data = await getCheckoutStatus(sessionId);
        if (!mountedRef.current) return;

        if (data.subscription_active) {
          clearInterval(intervalRef.current);
          clearTimeout(timeoutRef.current);
          setStatus('success');
          showToast('Subscription activated successfully!', 'success');
          await fetchUsage();
          setTimeout(() => {
            if (mountedRef.current) navigate('/dashboard');
          }, 2000);
        } else if (data.status === 'expired') {
          clearInterval(intervalRef.current);
          clearTimeout(timeoutRef.current);
          setStatus('expired');
          showToast('Checkout session expired. Please try again.', 'error');
        }
      } catch {
        // Keep polling on transient errors
      }
    }

    // Start polling
    checkStatus();
    intervalRef.current = setInterval(checkStatus, POLL_INTERVAL_MS);

    // Timeout after 30 seconds
    timeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      clearInterval(intervalRef.current);
      setStatus('timeout');
      showToast('Activation is taking longer than expected.', 'error');
    }, TIMEOUT_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(intervalRef.current);
      clearTimeout(timeoutRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'expired' || status === 'timeout') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-8 max-w-md text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-900/50 flex items-center justify-center">
            <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            {status === 'expired' ? 'Session Expired' : 'Activation Timed Out'}
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            {status === 'expired'
              ? 'Your checkout session has expired. Please try again.'
              : 'Your subscription activation is taking longer than expected. It may still activate shortly.'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/pricing')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
          >
            Back to Pricing
          </button>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-8 max-w-md text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-900/50 flex items-center justify-center">
            <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Subscription Activated!</h2>
          <p className="text-gray-400 text-sm">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="bg-gray-900 rounded-xl border border-gray-800 p-8 max-w-md text-center">
        <div className="flex justify-center mb-4">
          <LoadingSpinner size="sm" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Activating your subscription...</h2>
        <p className="text-gray-400 text-sm">This usually takes just a few seconds.</p>
      </div>
    </div>
  );
}
